import { useEffect } from 'react';

interface SeoMetaProps {
  title: string;
  description: string;
  path: string;
  robots?: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SITE_URL = 'https://njordheim.com';

const upsertMeta = (selector: string, attrName: 'name' | 'property', attrValue: string) => {
  let meta = document.head.querySelector<HTMLMetaElement>(selector);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attrName, attrValue);
    document.head.appendChild(meta);
  }

  return meta;
};

const upsertCanonical = () => {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }

  return canonical;
};

const SeoMeta: React.FC<SeoMetaProps> = ({
  title,
  description,
  path,
  robots = 'index,follow',
  type = 'website',
  jsonLd,
}) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const pageUrl = new URL(normalizedPath, SITE_URL).toString();
  const jsonLdEntries = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const jsonLdSignature = JSON.stringify(jsonLdEntries);

  useEffect(() => {
    document.title = title;

    upsertMeta('meta[name="description"]', 'name', 'description').content = description;
    upsertMeta('meta[name="robots"]', 'name', 'robots').content = robots;
    upsertMeta('meta[property="og:type"]', 'property', 'og:type').content = type;
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name').content = 'Njordheim Arena';
    upsertMeta('meta[property="og:title"]', 'property', 'og:title').content = title;
    upsertMeta('meta[property="og:description"]', 'property', 'og:description').content = description;
    upsertMeta('meta[property="og:url"]', 'property', 'og:url').content = pageUrl;
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card').content = 'summary';
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title').content = title;
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description').content = description;
    upsertCanonical().href = pageUrl;

    const appendedScripts: HTMLScriptElement[] = [];
    jsonLdEntries.forEach((entry) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(entry);
      document.head.appendChild(script);
      appendedScripts.push(script);
    });

    return () => {
      appendedScripts.forEach((script) => script.remove());
    };
  }, [description, jsonLdSignature, pageUrl, robots, title, type, jsonLdEntries]);

  return null;
};

export default SeoMeta;